import {
  IFormFileDrawingItem,
  IProjectFileDrawingTempChunkCompleteRequest,
} from '@file/projectFile/projectFile.models';
import { Card, Icon, UseFormReturn } from '@gcl/react-core';
import { QueryKey } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { DownloadFile } from '@file/DownloadFile';
import { useProjectId } from '@config/config.hooks';
import React from 'react';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import { IUploadProjectFileAsyncProps } from '@file/projectFile/projectFile.services';
import { v4 as uuidV4 } from 'uuid';
import { FileUploadLoader } from '@file/FileUploadLoader';
import { TransmittalItemTableRowInUse } from '../TransmittalItemTableRowInUse';
import {
  getTransmittalGeneralDetail,
  useFormPrefix,
  useUploadProjectFileDrawingTemp,
} from '../transmittalItemTableForm.hooks';
import { ETransmittalItemTableFormKey } from '../transmittalItemTableForm.constants';
import { TransmittalItemTableRowTransmittalRevisionForApproval } from '../TransmittalItemTableRowTransmittalRevisionForApproval';

export interface ITransmittalItemTableFormMobileRow {
  item: IFormFileDrawingItem;
  useFormMethods: UseFormReturn;
  rowIndex: number;
  tableIndex: number;
  transmittalId: number;
  queryKey: QueryKey;
  setSelectedRow: React.Dispatch<React.SetStateAction<number>>;
  docType: IProjectTransmittalItemDocumentType;
  fileToUpload?: File;
  setFilesToUpload: React.Dispatch<
    React.SetStateAction<{
      [key: string]: File;
    }>
  >;
  setIsBlockRefetch: (value: React.SetStateAction<boolean>) => void;
  isShowDetailModal: boolean;
  setRowUploading: (index: number, isUploading: boolean) => void;
  isReadOnly: boolean;
  cellExtras: { [key: string]: any };
}
export const TransmittalItemTableFormMobileRow = ({
  item,
  useFormMethods,
  rowIndex,
  tableIndex,
  transmittalId,
  queryKey,
  docType,
  setSelectedRow,
  fileToUpload,
  setFilesToUpload,
  setIsBlockRefetch,
  isShowDetailModal,
  setRowUploading,
  isReadOnly,
  cellExtras,
}: ITransmittalItemTableFormMobileRow) => {
  const projectId = useProjectId();
  const { transmittalStatus, transmittalType, masterScheduleId, defaultFileType } =
    getTransmittalGeneralDetail(transmittalId);
  const { rowFormPrefix } = useFormPrefix(tableIndex, rowIndex);

  const {
    mutationUploadProjectFileDrawingTemp,
    projectFile,
    setProjectFile,
    uploadProgress,
    setUploadProgress,
    renderErrorConfirmationPortal,
    openErrorConfirmationPortal,
  } = useUploadProjectFileDrawingTemp(
    item,
    item.form?.projectFile?.uuid,
    tableIndex,
    rowIndex,
    useFormMethods,
    setFilesToUpload,
    setIsBlockRefetch,
    undefined,
  );
  const {
    mutate: mutateUploadProjectFileDrawingTemp,
    data: dataUploadProjectFileDrawingTemp,
    error: errorUploadProjectFileDrawingTemp,
    reset: resetUploadProjectFileDrawingTemp,
    isLoading: isLoadingUploadProjectFileDrawingTemp,
    isError: isErrorUploadProjectFileDrawingTemp,
    isSuccess: isSuccessUploadProjectFileDrawingTemp,
  } = mutationUploadProjectFileDrawingTemp;

  React.useEffect(() => {
    if (isShowDetailModal) return;
    if (!fileToUpload || !item.form?.projectFile?.uuid || !!projectFile) return;

    const projectFileUploadState = item.form.projectFile.uploadState;
    if (projectFileUploadState !== 'uploadItem' && projectFileUploadState !== 'uploadRevision') return;

    setIsBlockRefetch(true);
    const uploadRequest: IUploadProjectFileAsyncProps<IProjectFileDrawingTempChunkCompleteRequest> = {
      request: {
        fileType: defaultFileType,
        projectId,
        fileName: fileToUpload?.name ?? '',
        file: fileToUpload,
        uuid: item.form?.projectFile?.uuid ?? uuidV4(),
        isTemp: true,
      },
      onUploadProgress: (progress) => {
        setUploadProgress(progress);
      },
    };
    if (projectFileUploadState === 'uploadItem')
      useFormMethods.setValue(
        `${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`,
        'uploadingItem',
      );
    if (projectFileUploadState === 'uploadRevision')
      useFormMethods.setValue(
        `${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`,
        'uploadingRevision',
      );
    setProjectFile(uploadRequest.request);
    mutateUploadProjectFileDrawingTemp(uploadRequest);
  }, [item.form?.projectFile?.uuid]);

  return (
    <>
      <Card
        className="gcl-gems-transmittal-item-table-form-mobile-row"
        content={
          <div>
            <div className="gcl-gems-transmittal-item-table-form-mobile-row-top">
              <div className="gcl-gems-transmittal-item-table-form-mobile-row-doc-no">{item.form?.item?.docNo}</div>
              <FileUploadLoader
                uploadProgress={uploadProgress}
                error={errorUploadProjectFileDrawingTemp}
                fileName={projectFile?.fileName}
                render={() => {
                  if (!item.transmittalItem)
                    return (
                      <Icon
                        data-testid={`edit-transmittal-item-btn-table-${tableIndex}-row-${rowIndex}`}
                        iconName="Pencil"
                        className="gcl-ic-btn-lg gcl-fc-danger-400"
                        onClick={(e) => {
                          setSelectedRow(rowIndex);
                        }}
                      />
                    );
                  const revision = item.transmittalItem?.inUse ? (
                    <>
                      {!isPositiveNumber(item.transmittalItem?.transmittalRevision?.projectFile?.id) ? (
                        <>
                          <Icon
                            iconName="DashCircle"
                            className="gcl-gems-transmittal-item-download-column-not-issued-icon"
                          />
                        </>
                      ) : (
                        <DownloadFile
                          shouldNotChop
                          text={item.transmittalItem?.transmittalRevision?.revisionNo}
                          projectId={projectId || 0}
                          attachmentId={item.transmittalItem?.transmittalRevision?.projectFile?.id || 0}
                        />
                      )}
                    </>
                  ) : (
                    <Icon iconName="LockFill" className="gcl-gems-transmittal-item-table-form-mobile-row-not-in-use" />
                  );
                  return (
                    <>
                      <div>
                        {(transmittalType === ETransmittalType.DESIGNER ||
                          transmittalType === ETransmittalType.SITE ||
                          !docType.allowUpload) &&
                          revision}
                        {(transmittalType === ETransmittalType.ICE || transmittalType === ETransmittalType.REVIEWER) &&
                          docType.allowUpload && (
                            <TransmittalItemTableRowTransmittalRevisionForApproval
                              cellExtras={cellExtras}
                              projectId={projectId}
                              masterScheduleId={masterScheduleId}
                              tempValue={item.form?.revisionNo ?? ''}
                              item={item}
                              index={rowIndex}
                              transmittalId={transmittalId}
                              transmittalType={transmittalType}
                              transmittalStatus={transmittalStatus}
                              setRowUploading={setRowUploading}
                              docType={docType}
                              onRenderIdle={() =>
                                item.transmittalItem?.inUse ? (
                                  revision
                                ) : (
                                  <Icon
                                    iconName="LockFill"
                                    className="gcl-gems-transmittal-item-table-form-mobile-row-not-in-use"
                                  />
                                )
                              }
                              isReadOnly={isReadOnly}
                              queryKey={queryKey}
                            />
                          )}
                      </div>
                      {(transmittalType === ETransmittalType.DESIGNER || transmittalType === ETransmittalType.SITE) &&
                        docType.allowUpload && (
                          <TransmittalItemTableRowInUse
                            useFormMethods={useFormMethods}
                            item={item}
                            transmittalId={transmittalId}
                            transmittalStatus={transmittalStatus}
                            transmittalType={transmittalType}
                            isAnyActionLoading={false}
                            queryKey={queryKey}
                            rowIndex={rowIndex}
                            setRowState={undefined}
                            rowState="Idle"
                          />
                        )}

                      <Icon
                        data-testid={`edit-transmittal-item-btn-table-${tableIndex}-row-${rowIndex}`}
                        iconName={
                          transmittalStatus === ETransmittalStatus.DRAFT &&
                          docType.allowUpload &&
                          item.transmittalItem.inUse &&
                          transmittalType !== ETransmittalType.REVIEWER
                            ? 'Pencil'
                            : 'ArrowsAngleExpand'
                        }
                        className={`gcl-ic-btn-lg ${
                          item.form?.projectFile ? 'gcl-fc-danger-400' : 'gcl-fc-primary-400'
                        }`}
                        onClick={(e) => {
                          setSelectedRow(rowIndex);
                        }}
                      />
                      {errorUploadProjectFileDrawingTemp && (
                        <Icon
                          iconName="ExclamationTriangle"
                          className="gcl-gems-transmittal-item-upload-revision-fail-icon gcl-ic-btn-lg"
                          onClick={openErrorConfirmationPortal}
                        />
                      )}
                    </>
                  );
                }}
              />
            </div>
            {item.form?.item?.description}
          </div>
        }
      />
      {renderErrorConfirmationPortal(false, false, false)}
    </>
  );
};
